/**
 * Configuration Manager
 * 
 * This module provides low-level functionality for configuration management:
 * 1. Fetching configuration from API endpoints
 * 2. Caching configuration in sessionStorage with 1-hour expiry
 * 3. Providing fallback to cached configuration when API is unavailable
 * 
 * Note: For React components, use the useConfig hook from @/utils/useConfig instead.
 * The hook provides RTK Query-powered caching with automatic request deduplication,
 * loading states, and error handling.
 * 
 * Usage:
 * - getLocalConfig(configName): Get cached config from sessionStorage
 * - refreshConfig(configName): Force refresh from API
 * - getConfigWithRefresh(configName): Get config with automatic refresh if stale
 */

import apiFetch from './apiFetch';
import { getSessionStorage, setSessionStorage } from './sessionStorage';
/**
 * For the page data
 * @param title Is the title of the page.
 * @param tab (optional) Is the title that should be used in the tabs. If it is not provided, the title isused.
 * @param help (otional) Is the text that would be displayed on the tooltip. If not provided, the tooltip is not displayed.
 * @param id Is the ID of the section, is used for navigational purposes.
 * @param breadcrumb (optional) Is the text displayed in the breadcrump. If not provided, the title is used.
 */
export interface PageData {
    /** Is the title of the page. */
    title: string;

    /** Is the title that should be used in the tabs. If it is not provided, the title isused. */
    tab?: string;

    /** Is the text that would be displayed on the tooltip. If not provided, the tooltip is not displayed. */
    help?: string;

    /** Is the ID of the section, is used for navigational purposes. */
    id: string;

    /** Is the text displayed in the breadcrump. If not provided, the title is used. */
    breadcrumb?: string;
}

/**
 * Configuration for the entire
 */
export interface Configuration {
    /**
     * Which portal this website is for
     */
    portal: string;

    /**
     * Display name
     */
    name: string;

    /** Holds image configuration */
    images: {
        header: string;
        logo: string;
    };

    /**
     * Short name
     */
    shortName: string;

    /** Configuration for the sign up page */
    signUp: {
        displayTradingName: boolean;
        displayJobTitle: boolean;
        validation: {
            token: string;
        };
    };

    /** Configuration for the landing section on the sign up and sign in pages */
    landing: {
        title: string;
        description: string;
    };

    /** Configuration for the sign in page */
    signIn: {
        image: string;
        metadata: {
            title: string;
            description: string;
            keywords: string;
        };
        auth: Record<string, boolean>;
        signUp: {
            showSignUp: boolean;
            signUpInfoLink?: string;
            signUpToken: string;
        };
    };

    /** Configuration for the forgot password page */
    forgotPassword: {
        metadata: {
            title: string;
            description: string;
            keywords: string;
        };
    };

    /** Configuration for the terms */
    terms: {
        title: string;
        content: string;
    };

    /** Configuration for the privacy */
    privacy: {
        title: string;
        content: string;
    };

    /** Configuration for the web rar page */
    webrar: {
        /** The title for this page */
        title: string;

        /** Holds the options for the tabs */
        tabs: {
            /** Dashboard tab configuration */
            dashboard: PageData;

            /** New Domain tab configuration */
            newDomain: PageData;

            /** Current Domain tab configuration */
            currentDomains: PageData;

            /** Contacts tab configuration */
            contacts: PageData;

            /** Messages tab configuration */
            messages: PageData;

            /** Pricelist tab configuration */
            pricelist: PageData;
        };
    };

    /** Configuration for the billing page */
    billing: {
        /** The title for this page */
        title: string;

        /** Holds the options for the tabs */
        tabs: {
            /** Configuration for the details tab */
            details: PageData;

            /** Configuration for the successful payments tab */
            successfulPayments: PageData;

            /** Configuration for the failed payments tab */
            failedPayments: PageData;

            /** Configuration for the documents tab */
            financialDocuments: PageData;

            /** Configuration for the billable transactions tab */
            billableTransactions: PageData;

            /** Configuration for the pricelist tab */
            pricelist: PageData;
        };
    };

    /** Configuration for the account page */
    account: {
        /** The title for this page */
        title: string;

        /** Holds the options for the tabs */
        tabs: {
            /** Configuartion for the details tab */
            details: PageData;

            /** Configuration for the address tab */
            address: PageData;

            /** Configuration for the contacts tab */
            contacts: PageData;

            /** Configuration for the epp tab */
            epp: PageData;
        };
    };

    /** Configuration for authentication */
    auth: {
        google: {
            clientId: string;
        };
    };
}

/**
 * Fetch configuration from the API
 * @param configName - The name of the configuration to fetch
 * @returns {Promise<ConfigType | null>} The configuration data or null if failed
 */
const fetchConfig = async (configName: string): Promise<ConfigType | null> => {
    // Validate config name - prevent empty/undefined/null config names from triggering API calls
    if (!configName || configName.trim() === '') {
        console.warn('Cannot fetch config: invalid config name provided');
        return null;
    }

    try {
        const response = await apiFetch('/portal/config/', {
            method: 'POST',
            body: JSON.stringify({
                config_name: configName
            })
        });

        if (response.ok) {
            const data = await response.json();
            return data as ConfigType;
        }

        return null;
    } catch (_error) {
        console.warn('Failed to fetch config:', _error);
        return null;
    }
};

export interface ConfigValues {
    [key: string]: string | string[] | number | boolean | ConfigValues | ConfigValues[];
}

export type ConfigType = {
    id: number,
    cdate: Date,
    config_name: string,
    display_name: string,
    description: string,
    config_value: ConfigValues;
};

export type InternalConfigType = {
    last_checked: Date | string, // Can be Date object or ISO string after localStorage serialization
    config: ConfigType;
};

/**
 * Type for the dynamic configuration that matches the static Configuration interface
 */
export interface DynamicConfiguration extends Configuration {
    // This ensures the dynamic config has the same structure as static config
}

/**
 * Get configuration by name from localStorage or return default
 * @param configName - The name of the configuration to retrieve
 * @returns The configuration object
 */
export const getLocalConfig = (configName: string): ConfigValues => {
    // Validate config name - return empty object for invalid names
    if (!configName || configName.trim() === '') {
        return {};
    }

    const internalConfig = getSessionStorage(configName) as InternalConfigType;
    return internalConfig?.config.config_value || {};
};

/**
 * Update configuration cache with fetched data
 * @param config - The configuration data to cache
 * @returns Whether the update was successful
 */
const updateConfig = (config: ConfigType, configName: string): boolean => {
    // Validate config name - prevent updating invalid config names
    if (!configName || configName.trim() === '') {
        console.warn('Cannot update config: invalid config name provided');
        return false;
    }

    try {
        const internalConfig: InternalConfigType = {
            last_checked: new Date(),
            config: config
        };

        setSessionStorage(configName, internalConfig);

        // Dispatch custom event to notify hooks of config update
        const event = new CustomEvent('configUpdated', {
            detail: { configName }
        });
        globalThis.dispatchEvent(event);

        return true;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update config cache:', error);
        return false;
    }
};

/**
 * Check if configuration is stale (older than 1 hour)
 * @param configName - The name of the configuration to check
 * @returns Whether the configuration is stale
 */
export const isConfigStale = (configName: string): boolean => {
    // Return true for invalid config names (consider them stale)
    if (!configName || configName.trim() === '') {
        return true;
    }

    try {
        const internalConfig = getSessionStorage(configName) as InternalConfigType;

        if (!internalConfig?.last_checked) {
            return true; // No cached config, consider it stale
        }

        // Handle both Date objects and ISO strings from localStorage
        // JSON.stringify/parse converts Date objects to ISO strings
        let lastChecked: Date;

        if (internalConfig.last_checked instanceof Date) {
            lastChecked = internalConfig.last_checked;
        } else {
            // Parse the ISO string back to a Date object
            lastChecked = new Date(internalConfig.last_checked);
        }

        // Validate that we have a valid date
        if (Number.isNaN(lastChecked.getTime())) {
            console.warn('Invalid date in config cache for', configName);
            return true;
        }

        // Compare timestamps directly to avoid timezone issues
        const now = Date.now();
        const lastCheckedTime = lastChecked.getTime();
        const oneHourInMs = 60 * 60 * 1000; // 1 hour in milliseconds

        const timeDiffMs = now - lastCheckedTime;

        const isStale = timeDiffMs > oneHourInMs;

        return isStale;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to check config staleness:', error);
        return true; // Consider stale if we can't check
    }
};

/**
 * TEMPORARY: Force a config to appear stale for testing purposes
 * @param configName - The name of the configuration to make stale
 */
export const forceConfigStale = (configName: string): void => {
    try {
        const internalConfig = getSessionStorage(configName) as InternalConfigType;

        if (internalConfig) {
            // Set last_checked to just over 1 hour ago (65 minutes to be safe)
            const overOneHourAgo = new Date();
            overOneHourAgo.setMinutes(overOneHourAgo.getMinutes() - 65);

            const updatedConfig = {
                ...internalConfig,
                last_checked: overOneHourAgo
            };

            setSessionStorage(configName, updatedConfig);
            console.warn('Forced config to be stale (65 minutes old):', configName);
        }
    } catch (error) {
        console.warn('Failed to force config stale:', error);
    }
};

export const refreshConfig = async (configName: string) => {
    // Validate config name - prevent empty/undefined/null config names
    if (!configName || configName.trim() === '') {
        console.warn('Cannot refresh config: invalid config name provided');
        return false;
    }

    try {
        const fetchedConfig = await fetchConfig(configName);

        if (fetchedConfig) {
            updateConfig(fetchedConfig, configName);
            return true;
        }

        return false;

    } catch (_error) {
        console.warn('Failed to refresh config:', _error);
        return false;
    }
};

/**
 * Get configuration with automatic refresh if stale
 * @param configName - The name of the configuration to get
 * @returns The configuration values, error if fetch failed, and a refresh function
 * @throws Error if config could not be fetched and no cached config exists
 */
export const getConfigWithRefresh = async (configName: string): Promise<{
    config: ConfigValues;
    refresh: () => Promise<boolean>;
}> => {
    // Validate config name - prevent empty/undefined/null config names
    if (!configName || configName.trim() === '') {
        console.warn('Cannot get config: invalid config name provided');
        return {
            config: {},
            refresh: () => Promise.resolve(false)
        };
    }

    // Check if config is stale and refresh if needed
    if (isConfigStale(configName)) {
        const refreshSuccess = await refreshConfig(configName);

        // If refresh failed, check if we have a cached config
        if (!refreshSuccess) {
            const cachedConfig = getLocalConfig(configName);

            // Only throw error if there's no cached config to fall back to
            if (!cachedConfig || Object.keys(cachedConfig).length === 0) {
                throw new Error(`Failed to fetch configuration for "${configName}" and no cached config available`);
            }
        }
    }

    const config = getLocalConfig(configName);
    const refresh = () => refreshConfig(configName);

    return { config, refresh };
};

/**
 * ==== Secrets ====
 */


export { getSecrets } from './secretsManager';
export { fetchConfig, updateConfig };
